// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email, password, displayName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, { displayName });

    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: displayName,
      createdAt: new Date().toISOString(),
      bio: '',
      location: '',
      favoriteSpecies: [],
      stats: {
        totalCatches: 0,
        totalStreams: 0,
        followers: 0,
        following: 0
      }
    });

    return user;
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const loadUserProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const reauthenticate = async (password) => {
    if (!auth.currentUser?.email) throw new Error('Not signed in');
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
  };

  const changeEmail = async (newEmail, password) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    await reauthenticate(password);
    await updateEmail(user, newEmail);
    await updateDoc(doc(db, 'users', user.uid), { email: newEmail });
  };

  const changePassword = async (currentPassword, newPassword) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    await reauthenticate(currentPassword);
    await updatePassword(user, newPassword);
  };

  const updateUserProfile = async (displayName, bio) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    if (displayName != null && displayName.trim() !== '') {
      await updateProfile(user, { displayName: displayName.trim() });
    }
    await updateDoc(doc(db, 'users', user.uid), {
      ...(displayName != null && { displayName: displayName.trim() }),
      ...(bio != null && { bio: bio.trim() }),
      updatedAt: new Date().toISOString()
    });
    await loadUserProfile(user.uid);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    loadUserProfile,
    changeEmail,
    changePassword,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
